// The MSG91 account, and — at the top, before anything else — a plain-English
// answer to "would a message actually be delivered right now?".
//
// That banner is the point of this screen. Every value here can be filled in
// correctly and still deliver nothing, because two switches default to off, so
// a form that only showed fields would leave an admin certain they were
// configured and quietly sending nothing.
import { useEffect, useState } from 'react';
import { Radio, ShieldAlert, TriangleAlert, CircleCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useGateway, useSaveGateway, useTestConnection } from './hooks';
import type { SmsGatewaySettings } from './types';

type Draft = Partial<SmsGatewaySettings> & { auth_key?: string };

export default function ConnectionTab() {
  const gateway = useGateway();
  const save = useSaveGateway();
  const test = useTestConnection();

  const [draft, setDraft] = useState<Draft>({});
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  /**
   * Seeded from the server, but NOT while the form has unsaved edits.
   *
   * The query client refetches on window focus with a 30-second stale time,
   * and this screen's actual workflow is "switch to the MSG91 panel, copy the
   * key, switch back" — which is a focus event, well past 30 seconds, landing
   * on a half-filled form. Without the `dirty` guard that refetch silently
   * replaces everything typed so far, including a pasted credential.
   */
  useEffect(() => {
    if (gateway.data && !dirty) setDraft({ ...gateway.data.settings, auth_key: '' });
  }, [gateway.data, dirty]);

  if (gateway.isLoading) return <LoadingState label="Loading SMS gateway settings…" />;
  if (gateway.isError) {
    return (
      <ErrorState
        message={gateway.error instanceof ApiError ? gateway.error.message : 'Could not load SMS gateway settings'}
        onRetry={() => gateway.refetch()}
      />
    );
  }

  const status = gateway.data!.status;
  const settings = gateway.data!.settings;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setDirty(true);
    setDraft((p) => ({ ...p, [k]: v }));
  };

  // The saved key is never sent to the browser, so an untouched field means
  // "leave it alone" rather than "clear it".
  const payload = (): Draft => {
    const { auth_key, ...rest } = draft;
    return auth_key ? { ...rest, auth_key } : rest;
  };

  // Re-seeded explicitly from what came back rather than by letting the effect
  // above do it: the effect is deliberately inert while `dirty` is set, and
  // clearing `dirty` alone would not re-run it (its other dependency has not
  // changed by then). The auth key field is always emptied — the server never
  // returns the key, so a stale value left in the box means nothing.
  const onSave = async () => {
    const res = await save.mutateAsync(payload());
    setDraft({ ...res.settings, auth_key: '' });
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const onTest = async () => {
    // This endpoint saves before it tests, so whatever was typed is now the
    // stored state whether or not the credential check itself passed.
    const res = await test.mutateAsync(payload());
    setDraft((p) => (res.settings ? { ...res.settings, auth_key: '' } : { ...p, auth_key: '' }));
    setDirty(false);
  };

  const busy = save.isPending || test.isPending;
  const result = test.data;

  return (
    <div className="max-w-3xl space-y-4">
      {/* ── Would a message actually go out? ───────────────────── */}
      <div
        className={
          'rounded-xl border p-4 ' +
          (status.can_send && !status.simulate_only
            ? 'border-success-soft bg-success-soft'
            : status.can_send
              ? 'border-warn-soft bg-warn-soft'
              : 'border-danger-soft bg-danger-soft')
        }
      >
        <div className="flex items-start gap-2.5">
          {status.can_send && !status.simulate_only ? (
            <CircleCheck size={17} className="mt-px shrink-0 text-success-ink" />
          ) : status.can_send ? (
            <TriangleAlert size={17} className="mt-px shrink-0 text-warn-ink" />
          ) : (
            <ShieldAlert size={17} className="mt-px shrink-0 text-danger-ink" />
          )}
          <div className="min-w-0">
            <div
              className={
                'text-[13px] font-semibold ' +
                (status.can_send && !status.simulate_only
                  ? 'text-success-ink'
                  : status.can_send ? 'text-warn-ink' : 'text-danger-ink')
              }
            >
              {status.summary}
            </div>

            {status.blockers.length > 1 && (
              <ul className="mt-2 list-disc space-y-1 pl-4 text-[12px] text-danger-ink">
                {status.blockers.slice(1).map((b) => <li key={b.code}>{b.message}</li>)}
              </ul>
            )}

            {status.warnings.filter((w) => w.code !== 'simulate_only').map((w) => (
              <div key={w.code} className="mt-2 text-[12px] text-warn-ink">⚠ {w.message}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── The account ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle><Radio size={15} /> MSG91 account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            label="Auth key"
            error={save.error instanceof ApiError ? save.error.fieldErrors.auth_key : undefined}
          >
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={draft.auth_key ?? ''}
                onChange={(e) => set('auth_key', e.target.value)}
                placeholder={settings.auth_key_set ? 'A key is saved — type here only to replace it' : 'Paste the auth key from MSG91'}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted hover:text-ink"
              >
                {showKey ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="mt-1 text-[11px] text-ink-muted">
              {settings.auth_key_set
                ? settings.auth_key_source === 'environment'
                  ? 'Currently using the key from the server environment (MSG91_AUTH_KEY). Saving one here takes precedence over it.'
                  : 'A key is saved and encrypted. It is never sent back to this screen — leave the field blank to keep it.'
                : 'No key configured yet. Nothing can be delivered until one is saved.'}
            </div>
          </FormField>

          <div className="grid2">
            <FormField
              label="Sender header"
              error={save.error instanceof ApiError ? save.error.fieldErrors.sender_id : undefined}
            >
              <Input
                value={draft.sender_id ?? ''}
                onChange={(e) => set('sender_id', e.target.value.toUpperCase())}
                placeholder="PULMAI"
                maxLength={11}
              />
              <div className="mt-1 text-[11px] text-ink-muted">
                Exactly 6 letters, and it must match the header registered on the DLT portal character for
                character. A wrong one is rejected by the operator while MSG91 still reports success.
              </div>
            </FormField>

            <FormField label="DLT principal entity ID">
              <Input
                value={draft.dlt_pe_id ?? ''}
                onChange={(e) => set('dlt_pe_id', e.target.value)}
                placeholder="From the Airtel DLT portal"
              />
              <div className="mt-1 text-[11px] text-ink-muted">
                Not sent with each message — MSG91 holds it against the template. Recorded here for
                reconciling a rejection against the portal.
              </div>
            </FormField>

            <FormField label="Route">
              <NativeSelect value={draft.route ?? '4'} onChange={(e) => set('route', e.target.value)}>
                <option value="4">Transactional (4)</option>
                <option value="1">Promotional (1)</option>
              </NativeSelect>
            </FormField>

            <FormField label="Country code">
              <Input
                value={draft.country_code ?? '91'}
                onChange={(e) => set('country_code', e.target.value)}
                placeholder="91"
                maxLength={5}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* ── The two switches ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery switches</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold">Send SMS</div>
              <div className="mt-0.5 text-[12px] text-ink-muted">
                The master switch. While it is off, nothing leaves this server — messages are still built
                and logged with the reason, so the delivery log stays a complete record.
              </div>
            </div>
            <Switch
              checked={!!draft.send_enabled}
              onCheckedChange={(v) => set('send_enabled', v)}
              aria-label="Send SMS"
            />
          </label>

          <label className="flex items-start justify-between gap-4 border-t border-border pt-4">
            <div>
              <div className="text-[13px] font-semibold">Simulate only</div>
              <div className="mt-0.5 text-[12px] text-ink-muted">
                Build, verify and cost every message, then stop. Nothing is delivered and nothing is
                charged. Turn this off last, once a test message has arrived on a staff handset.
              </div>
            </div>
            <Switch
              checked={!!draft.simulate_only}
              onCheckedChange={(v) => set('simulate_only', v)}
              aria-label="Simulate only"
            />
          </label>
        </CardContent>
      </Card>

      {/* ── Test result ─────────────────────────────────────────── */}
      {result && (
        <div
          className={
            'rounded-xl border p-3 text-[13px] ' +
            (result.ok ? 'border-success-soft bg-success-soft text-success-ink' : 'border-danger-soft bg-danger-soft text-danger-ink')
          }
        >
          {result.ok ? (
            <>
              ✓ Connected to MSG91. Account balance on route {result.route}: <strong>{result.balance}</strong>.
              {result.sender_id && <> Sender header <strong>{result.sender_id}</strong>.</>}
            </>
          ) : (
            <>✗ {result.error}</>
          )}
        </div>
      )}
      {test.isError && (
        <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
          ✗ {test.error instanceof ApiError ? test.error.message : 'The connection test could not be run.'}
        </div>
      )}
      {save.isError && !(save.error instanceof ApiError && Object.keys(save.error.fieldErrors).length) && (
        <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
          ✗ {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onSave} loading={save.isPending} disabled={busy}>
          {saved ? '✓ Saved' : 'Save settings'}
        </Button>
        {/*
          Labelled "Save & test" because that is what it does. It used to test
          the SAVED key while the admin looked at a freshly pasted one, which
          reported "no key configured" about a state nobody had asked about.
        */}
        <Button variant="ghost" onClick={onTest} loading={test.isPending} disabled={busy}>
          ⚡ Save &amp; test connection
        </Button>
      </div>

      {settings.updated_at && (
        <div className="text-[11px] text-ink-muted">
          Last changed {new Date(settings.updated_at).toLocaleString('en-IN')}
          {settings.updated_by ? ` by ${settings.updated_by}` : ''}.
        </div>
      )}
    </div>
  );
}
